import type { TextAnchor } from '../HtmlViewerTypes';

const CONTEXT_LENGTH = 32;

// ─── domPath helpers ──────────────────────────────────────────────────────────

function buildDomPath(node: Node, root: HTMLElement): string {
  const parts: string[] = [];
  let current: Node | null = node;

  while (current && current !== root) {
    const parent: Element | null = current.parentElement;
    if (!parent) break;

    const tag = (current as Element).tagName?.toLowerCase() ?? 'text';
    const siblings = Array.from(parent.childNodes).filter(
      (n: ChildNode) => n.nodeType === Node.TEXT_NODE
        ? n.nodeType === current!.nodeType
        : (n as Element).tagName === (current as Element).tagName
    );
    const index = siblings.indexOf(current as ChildNode);
    parts.unshift(`${tag}:nth(${index})`);
    current = parent;
  }

  return parts.join(' > ');
}

function resolveByDomPath(
  domPath: string,
  root: HTMLElement
): Node | null {
  // Walk the path step by step
  const steps = domPath.split(' > ');
  let current: Node = root;

  for (const step of steps) {
    const match = step.match(/^(.+):nth\((\d+)\)$/);
    if (!match) return null;

    const [, tag, idxStr] = match;
    const idx = parseInt(idxStr, 10);

    const children = Array.from(current.childNodes).filter(n =>
      tag === 'text'
        ? n.nodeType === Node.TEXT_NODE
        : (n as Element).tagName?.toLowerCase() === tag
    );

    if (idx >= children.length) return null;
    current = children[idx];
  }

  return current;
}

interface TextSegment {
  node: Text;
  start: number;
  end: number;
}

interface BoundaryPoint {
  node: Node;
  offset: number;
}

function getBoundaryOffsets(
  range: Range,
  contentRoot: HTMLElement
): { start: number; end: number } {
  const startProbe = document.createRange();
  startProbe.selectNodeContents(contentRoot);
  startProbe.setEnd(range.startContainer, range.startOffset);

  const endProbe = document.createRange();
  endProbe.selectNodeContents(contentRoot);
  endProbe.setEnd(range.endContainer, range.endOffset);

  return {
    start: startProbe.toString().length,
    end: endProbe.toString().length,
  };
}

function collectTextSegments(contentRoot: HTMLElement): TextSegment[] {
  const walker = document.createTreeWalker(
    contentRoot,
    NodeFilter.SHOW_TEXT,
    null
  );

  const segments: TextSegment[] = [];
  let offset = 0;
  let node: Text | null;

  while ((node = walker.nextNode() as Text | null)) {
    const text = node.textContent ?? '';
    const len = text.length;
    if (len === 0) continue;

    segments.push({
      node,
      start: offset,
      end: offset + len,
    });
    offset += len;
  }

  return segments;
}

function resolvePointFromGlobalOffset(
  segments: TextSegment[],
  globalOffset: number,
  preferEnd: boolean
): BoundaryPoint | null {
  if (segments.length === 0) return null;

  if (globalOffset <= 0) {
    return { node: segments[0].node, offset: 0 };
  }

  const totalLength = segments[segments.length - 1].end;
  if (globalOffset >= totalLength) {
    const last = segments[segments.length - 1];
    return { node: last.node, offset: last.end - last.start };
  }

  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    if (globalOffset < seg.end) {
      return { node: seg.node, offset: globalOffset - seg.start };
    }
    if (globalOffset === seg.end) {
      if (!preferEnd && i + 1 < segments.length) {
        return { node: segments[i + 1].node, offset: 0 };
      }
      return { node: seg.node, offset: seg.end - seg.start };
    }
  }

  return null;
}

function isValidOffset(node: Node, offset: number): boolean {
  if (offset < 0) return false;
  if (node.nodeType === Node.TEXT_NODE) {
    return offset <= (node.textContent?.length ?? 0);
  }
  return offset <= node.childNodes.length;
}

function extractContext(
  range: Range,
  contentRoot: HTMLElement
): { prefix: string; suffix: string } {
  const fullText = contentRoot.textContent ?? '';
  const { start, end } = getBoundaryOffsets(range, contentRoot);
  return {
    prefix: fullText.slice(Math.max(0, start - CONTEXT_LENGTH), start),
    suffix: fullText.slice(end, end + CONTEXT_LENGTH),
  };
}

function scoreCandidate(
  fullText: string,
  index: number,
  exactLength: number,
  anchor: TextAnchor
): number {
  let score = 0;

  if (anchor.prefix) {
    const actualPrefix = fullText.slice(
      Math.max(0, index - anchor.prefix.length),
      index
    );
    if (actualPrefix === anchor.prefix) score += 2;
  }

  if (anchor.suffix) {
    const actualSuffix = fullText.slice(
      index + exactLength,
      index + exactLength + anchor.suffix.length
    );
    if (actualSuffix === anchor.suffix) score += 2;
  }

  return score;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Creates a TextAnchor from a DOM Range for later restoration.
 */
export function saveAnchor(range: Range, contentRoot: HTMLElement): TextAnchor {
  const exact = range.toString();
  const { prefix, suffix } = extractContext(range, contentRoot);
  const startPath = buildDomPath(range.startContainer, contentRoot);
  const endPath = buildDomPath(range.endContainer, contentRoot);

  return {
    exact,
    prefix,
    suffix,
    startPath,
    endPath,
    // Keep legacy field for backward compatibility with stored anchors.
    domPath: startPath,
    startOffset: range.startOffset,
    endOffset: range.endOffset,
  };
}

/**
 * Restores a DOM Range from a TextAnchor.
 * Returns null if the anchor cannot be resolved (annotation becomes 'detached').
 *
 * Strategy A: domPath + offset
 * Strategy B (fallback): full-text fuzzy match using exact + prefix + suffix
 */
export function restoreRange(
  anchor: TextAnchor,
  contentRoot: HTMLElement
): Range | null {
  // Strategy A
  if (
    anchor.startPath &&
    anchor.endPath &&
    anchor.startOffset !== undefined &&
    anchor.endOffset !== undefined
  ) {
    try {
      const startNode = resolveByDomPath(anchor.startPath, contentRoot);
      const endNode = resolveByDomPath(anchor.endPath, contentRoot);

      if (
        startNode &&
        endNode &&
        isValidOffset(startNode, anchor.startOffset) &&
        isValidOffset(endNode, anchor.endOffset)
      ) {
        const range = document.createRange();
        range.setStart(startNode, anchor.startOffset);
        range.setEnd(endNode, anchor.endOffset);

        const text = range.toString();
        if (text === anchor.exact || text.includes(anchor.exact)) {
          return range;
        }
      }
    } catch {
      // Fall through to Strategy B
    }
  }

  // Strategy A (legacy anchors): single domPath
  if (anchor.domPath && anchor.startOffset !== undefined) {
    try {
      const node = resolveByDomPath(anchor.domPath, contentRoot);
      if (node && isValidOffset(node, anchor.startOffset)) {
        const endOffset =
          anchor.endOffset ?? Math.min(anchor.startOffset + anchor.exact.length, node.textContent?.length ?? 0);
        if (isValidOffset(node, endOffset)) {
          const range = document.createRange();
          range.setStart(node, anchor.startOffset);
          range.setEnd(node, endOffset);
          if (range.toString().includes(anchor.exact)) {
            return range;
          }
        }
      }
    } catch {
      // Fall through to Strategy B
    }
  }

  // Strategy B: walk all text nodes, fuzzy-match with prefix + exact + suffix
  return fuzzyRestoreRange(anchor, contentRoot);
}

function fuzzyRestoreRange(
  anchor: TextAnchor,
  contentRoot: HTMLElement
): Range | null {
  const exact = anchor.exact;
  if (!exact) return null;

  const fullText = contentRoot.textContent ?? '';
  if (!fullText) return null;

  const segments = collectTextSegments(contentRoot);
  if (segments.length === 0) return null;

  let bestIndex = -1;
  let bestScore = -1;

  let from = 0;
  while (from <= fullText.length - exact.length) {
    const idx = fullText.indexOf(exact, from);
    if (idx === -1) break;

    const score = scoreCandidate(fullText, idx, exact.length, anchor);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = idx;
    }

    from = idx + 1;
  }

  if (bestIndex === -1) return null;

  const startPoint = resolvePointFromGlobalOffset(segments, bestIndex, false);
  const endPoint = resolvePointFromGlobalOffset(
    segments,
    bestIndex + exact.length,
    true
  );

  if (!startPoint || !endPoint) return null;

  try {
    const range = document.createRange();
    range.setStart(startPoint.node, startPoint.offset);
    range.setEnd(endPoint.node, endPoint.offset);
    if (!range.toString()) return null;
    return range;
  } catch {
    return null;
  }
}
